'use client';

import { useState, Suspense } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { Input } from '@/components/platform/forms';
import SecretInput from '@/components/platform/forms/SecretInput';
import { PrimaryButton } from '@/components/platform/buttons';
import { signUp, signIn, sendVerificationEmail } from '@/lib/platform/auth/client';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Spinner from '@/components/platform/ui/Spinner';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { logger } from '@/lib/platform/utils/logger';
import TrustCenterModal from '@/components/platform/modals/TrustCenterModal';
import { DemoSignupModal } from '@/components/platform/demo-mode';
import confetti from 'canvas-confetti';

function SignupContent() {
  const { darkMode } = useDarkMode();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/welcome';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [salesforceLoading, setSalesforceLoading] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [error, setError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPage, setModalPage] = useState<'terms' | 'privacy' | 'dpa'>('terms');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await signUp.email({
        email,
        password,
        name,
      });

      if (error) {
        logger.error('Signup error:', error);

        // Parse Better Auth error messages
        let errorMessage = 'Failed to create account';
        if (typeof error === 'string') {
          errorMessage = error;
        } else if (error?.message) {
          errorMessage = error.message;
          // Check for common error patterns
          if (
            errorMessage.toLowerCase().includes('email') &&
            errorMessage.toLowerCase().includes('exists')
          ) {
            errorMessage = 'An account with this email already exists';
          } else if (errorMessage.toLowerCase().includes('invalid email')) {
            errorMessage = 'Please enter a valid email address';
          } else if (errorMessage.toLowerCase().includes('password')) {
            errorMessage = 'Password does not meet requirements';
          }
        }

        throw new Error(errorMessage);
      }

      if (data) {
        // Show success notification instead of redirecting
        setShowSuccess(true);
      }
    } catch (err) {
      logger.error('Signup error:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : 'An error occurred during signup';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      await signIn.social({
        provider: 'google',
        callbackURL: returnTo,
      });

      // If redirect was successful, page will navigate away
      // If we're still here after 3 seconds, something went wrong
      setTimeout(() => {
        setGoogleLoading(false);
      }, 3000);
    } catch (err) {
      logger.error('Google login error:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : 'An error occurred during Google login';
      setError(errorMessage);
      setGoogleLoading(false);
    }
  };

  const handleLinkedinLogin = async () => {
    setLinkedinLoading(true);
    setError('');

    try {
      await signIn.social({
        provider: 'linkedin',
        callbackURL: returnTo,
      });

      // If redirect was successful, page will navigate away
      // If we're still here after 3 seconds, something went wrong
      setTimeout(() => {
        setLinkedinLoading(false);
      }, 3000);
    } catch (err) {
      logger.error('LinkedIn login error:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : 'An error occurred during LinkedIn login';
      setError(errorMessage);
      setLinkedinLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setMicrosoftLoading(true);
    setError('');

    try {
      await signIn.social({
        provider: 'microsoft',
        callbackURL: returnTo,
      });

      // If redirect was successful, page will navigate away
      // If we're still here after 3 seconds, something went wrong
      setTimeout(() => {
        setMicrosoftLoading(false);
      }, 3000);
    } catch (err) {
      logger.error('Microsoft login error:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : 'An error occurred during Microsoft login';
      setError(errorMessage);
      setMicrosoftLoading(false);
    }
  };

  const handleSalesforceLogin = async () => {
    setSalesforceLoading(true);
    setError('');

    try {
      await signIn.social({
        provider: 'salesforce',
        callbackURL: returnTo,
      });

      // If redirect was successful, page will navigate away
      // If we're still here after 3 seconds, something went wrong
      setTimeout(() => {
        setSalesforceLoading(false);
      }, 3000);
    } catch (err) {
      logger.error('Salesforce login error:', err);
      const errorMessage =
        typeof err === 'string'
          ? err
          : err instanceof Error
            ? err.message
            : 'An error occurred during Salesforce login';
      setError(errorMessage);
      setSalesforceLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setError('');

    try {
      const { error } = await sendVerificationEmail({
        email,
        callbackURL: '/dashboard',
      });

      if (error) {
        throw new Error(
          typeof error === 'string'
            ? error
            : error?.message || 'Failed to resend verification email'
        );
      }

      // Show temporary success message (could add a toast here)
      logger.log('Verification email resent successfully');
    } catch (err) {
      logger.error('Resend verification error:', err);
      setError(
        err instanceof Error ? err.message : 'An error occurred while resending verification email'
      );
    } finally {
      setResendLoading(false);
    }
  };

  // If signup successful, show verification page
  if (showSuccess) {
    return (
      <div className="mx-auto w-full max-w-sm lg:w-96">
        <div>
          {/* Header */}
          <h2
            className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}
          >
            Check your email
          </h2>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            We&apos;ve sent a verification link to{' '}
            <strong className={darkMode ? 'text-white' : 'text-gray-900'}>{email}</strong>
          </p>
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            If you don&apos;t see the email, check your spam folder.
          </p>

          {/* Resend Button */}
          <div className="mt-6">
            <PrimaryButton
              onClick={handleResendVerification}
              disabled={resendLoading}
              className="w-full"
            >
              {resendLoading ? 'Sending...' : 'Resend email'}
            </PrimaryButton>
          </div>

          {/* Sign in link */}
          <p className={`mt-6 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Already have an account?{' '}
            <Link href="/auth/login" className="font-semibold text-[#2997ff] hover:text-[#1976d2]">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-sm lg:w-96" suppressHydrationWarning>
      {/* Header */}
      <div>
        <h1
          className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}
        >
          Create your account
        </h1>
        <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Already have an account?{' '}
          <Link href="/auth/login" className="text-[#2997ff] hover:text-[#1976d2]">
            Sign in
          </Link>
        </p>
      </div>

      {/* Signup Form */}
      <div className="mt-10">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Full name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="John Doe"
            required
            disabled={showSuccess}
          />

          <Input
            label="Email address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={showSuccess}
          />

          <SecretInput
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
            required
            showCopyButton={false}
            disabled={showSuccess}
          />

          {error && (
            <div
              className={`rounded-md p-4 ${darkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-800'}`}
            >
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div>
            <PrimaryButton
              type="submit"
              disabled={
                loading ||
                googleLoading ||
                linkedinLoading ||
                microsoftLoading ||
                salesforceLoading ||
                showSuccess
              }
              className="w-full"
            >
              {loading ? 'Creating account...' : showSuccess ? 'Check your email' : 'Sign up'}
            </PrimaryButton>

            {/* Terms & Privacy Notice */}
            <p
              className={`mt-3 text-center text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}
            >
              By signing up, you agree to our{' '}
              <button
                type="button"
                onClick={() => {
                  setModalPage('terms');
                  setModalOpen(true);
                }}
                className={`${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-600 hover:text-gray-700'} underline transition-colors`}
              >
                Terms of Use
              </button>
              ,{' '}
              <button
                type="button"
                onClick={() => {
                  setModalPage('privacy');
                  setModalOpen(true);
                }}
                className={`${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-600 hover:text-gray-700'} underline transition-colors`}
              >
                Privacy Policy
              </button>
              , and{' '}
              <button
                type="button"
                onClick={() => {
                  setModalPage('dpa');
                  setModalOpen(true);
                }}
                className={`${darkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-600 hover:text-gray-700'} underline transition-colors`}
              >
                Data Processing Agreement
              </button>
              .
            </p>
          </div>
        </form>

        {/* Divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div
                className={`w-full border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              />
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span
                className={`px-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}
              >
                Or continue with
              </span>
            </div>
          </div>
        </div>

        {/* Social Login Buttons */}
        <TooltipPrimitive.Provider delayDuration={300}>
          <div className="mt-6 grid grid-cols-4 gap-2">
            {/* Google */}
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  disabled={
                    loading ||
                    googleLoading ||
                    linkedinLoading ||
                    microsoftLoading ||
                    salesforceLoading ||
                    showSuccess
                  }
                  className={`flex w-full items-center justify-center rounded-md px-2 py-2 text-sm font-semibold ring-1 ring-inset ${
                    darkMode
                      ? 'bg-gray-800 text-white ring-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  }`}
                >
                  {googleLoading ? (
                    <Spinner size="xs" darkMode={darkMode} />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill={darkMode ? 'white' : 'black'}
                    >
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                  )}
                </button>
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                  className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                    darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                  }`}
                  sideOffset={5}
                >
                  Sign up with Google
                  <TooltipPrimitive.Arrow
                    className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                  />
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>

            {/* Microsoft */}
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                <button
                  type="button"
                  onClick={handleMicrosoftLogin}
                  disabled={
                    loading ||
                    googleLoading ||
                    linkedinLoading ||
                    microsoftLoading ||
                    salesforceLoading ||
                    showSuccess
                  }
                  className={`flex w-full items-center justify-center rounded-md px-2 py-2 text-sm font-semibold ring-1 ring-inset ${
                    darkMode
                      ? 'bg-gray-800 text-white ring-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  }`}
                >
                  {microsoftLoading ? (
                    <Spinner size="xs" darkMode={darkMode} />
                  ) : (
                    <svg
                      className="h-4 w-4"
                      viewBox="0 0 23 23"
                      fill={darkMode ? 'white' : 'black'}
                      aria-hidden="true"
                    >
                      <path d="M0 0h11v11H0z" />
                      <path d="M12 0h11v11H12z" />
                      <path d="M0 12h11v11H0z" />
                      <path d="M12 12h11v11H12z" />
                    </svg>
                  )}
                </button>
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                  className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                    darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                  }`}
                  sideOffset={5}
                >
                  Sign up with Microsoft
                  <TooltipPrimitive.Arrow
                    className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                  />
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>

            {/* LinkedIn */}
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                <button
                  type="button"
                  onClick={handleLinkedinLogin}
                  disabled={
                    loading ||
                    googleLoading ||
                    linkedinLoading ||
                    microsoftLoading ||
                    salesforceLoading ||
                    showSuccess
                  }
                  className={`flex w-full items-center justify-center rounded-md px-2 py-2 text-sm font-semibold ring-1 ring-inset ${
                    darkMode
                      ? 'bg-gray-800 text-white ring-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  }`}
                >
                  {linkedinLoading ? (
                    <Spinner size="xs" darkMode={darkMode} />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill={darkMode ? 'white' : 'black'}
                    >
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  )}
                </button>
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                  className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                    darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                  }`}
                  sideOffset={5}
                >
                  Sign up with LinkedIn
                  <TooltipPrimitive.Arrow
                    className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                  />
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>

            {/* Salesforce */}
            <TooltipPrimitive.Root>
              <TooltipPrimitive.Trigger asChild>
                <button
                  type="button"
                  onClick={handleSalesforceLogin}
                  disabled={
                    loading ||
                    googleLoading ||
                    linkedinLoading ||
                    microsoftLoading ||
                    salesforceLoading ||
                    showSuccess
                  }
                  className={`flex w-full items-center justify-center rounded-md px-2 py-2 text-sm font-semibold ring-1 ring-inset ${
                    darkMode
                      ? 'bg-gray-800 text-white ring-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                  }`}
                >
                  {salesforceLoading ? (
                    <Spinner size="xs" darkMode={darkMode} />
                  ) : (
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4"
                      fill={darkMode ? 'white' : 'black'}
                    >
                      <path d="M6 11.5c0-3.3 2.7-6 6-6 2.6 0 4.9 1.7 5.6 4.1.5-.1 1.1-.2 1.6-.2 3 0 5.5 2.5 5.5 5.5s-2.5 5.5-5.5 5.5H5.8c-3 0-5.5-2.5-5.5-5.5 0-2.9 2.3-5.3 5.2-5.4.2-.8.7-1.7 1.4-2.4z" />
                    </svg>
                  )}
                </button>
              </TooltipPrimitive.Trigger>
              <TooltipPrimitive.Portal>
                <TooltipPrimitive.Content
                  className={`z-[9999] rounded-lg px-3 py-2 text-xs font-medium shadow-lg ${
                    darkMode ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
                  }`}
                  sideOffset={5}
                >
                  Sign up with Salesforce
                  <TooltipPrimitive.Arrow
                    className={darkMode ? 'fill-gray-800' : 'fill-gray-900'}
                  />
                </TooltipPrimitive.Content>
              </TooltipPrimitive.Portal>
            </TooltipPrimitive.Root>
          </div>
        </TooltipPrimitive.Provider>

        {/* Divider */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div
                className={`w-full border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
              />
            </div>
            <div className="relative flex justify-center text-sm font-medium">
              <span
                className={`px-6 ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}
              >
                Or
              </span>
            </div>
          </div>

          {/* Demo Mode Button */}
          <div className="mt-6">
            <button
              onClick={() => {
                // Realistic confetti effect with multiple bursts
                const count = 200;
                const defaults = {
                  origin: { y: 0.7 },
                  colors: ['#2997ff', '#1976d2', '#42a5f5', '#90caf9', '#ffffff'],
                };

                function fire(particleRatio: number, opts: confetti.Options) {
                  confetti({
                    ...defaults,
                    ...opts,
                    particleCount: Math.floor(count * particleRatio),
                  });
                }

                // Multiple bursts for realistic effect
                fire(0.25, {
                  spread: 26,
                  startVelocity: 55,
                });
                fire(0.2, {
                  spread: 60,
                });
                fire(0.35, {
                  spread: 100,
                  decay: 0.91,
                  scalar: 0.8,
                });
                fire(0.1, {
                  spread: 120,
                  startVelocity: 25,
                  decay: 0.92,
                  scalar: 1.2,
                });
                fire(0.1, {
                  spread: 120,
                  startVelocity: 45,
                });

                setShowDemoModal(true);
              }}
              disabled={showSuccess}
              className={`flex w-full justify-center rounded-md px-2.5 py-1.5 text-sm font-semibold ring-1 ring-inset ${
                darkMode
                  ? 'bg-gray-800 text-white ring-gray-700 hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              Explore the demo
            </button>
          </div>
        </div>
      </div>

      {/* Trust Center Modal */}
      <TrustCenterModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        page={modalPage}
        darkMode={darkMode}
      />

      {/* Demo Signup Modal */}
      <DemoSignupModal isOpen={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </div>
  );
}

// Server-rendered fallback: because SignupContent uses useSearchParams, this
// fallback IS the HTML served to crawlers. It must carry the page's h1 and links.
function SignupFallback() {
  return (
    <div className="mx-auto w-full max-w-sm lg:w-96">
      <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        Create your account
      </h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Create a Valiyou account to measure your attention value, build data-driven sponsorship
        valuations and manage partnership proposals for your club or commercial team.
      </p>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Already have an account?{' '}
        <Link href="/auth/login" className="text-[#2997ff] underline hover:text-[#1976d2]">
          Sign in here
        </Link>
      </p>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<SignupFallback />}>
      <SignupContent />
    </Suspense>
  );
}
